import type { ArtStateValues } from '../core/ArtState.js';
import {
  REPLAY_SCHEMA_VERSION,
  type ReplayData,
  type ReplayInput,
  type ReplayPerformance,
  type ReplayResult,
  type ReplayRhythmEvent,
  type ReplaySong,
} from './ReplaySchema.js';

/**
 * Writes down a performance while it happens.
 *
 * Fed from the runtime's own input and event synthesis point rather than by
 * listening to the signal bus. That is a deliberate choice: the bus carries what
 * the *visuals* were told, after mapping and after any mode-specific decisions,
 * and recording there would bake this run's interpretation into the file. The
 * recorder wants the layer below — the press, and the judgement — so that a
 * different interpretation can be applied later.
 *
 * It stores no mutations, no renderer state and no frames. See `ReplaySchema`.
 */

export interface ReplayRecorderOptions {
  readonly engineVersion?: string;
  /** Cap on stored inputs, so a stuck key cannot grow a file without bound. */
  readonly maxInputs?: number;
  readonly maxEvents?: number;
  readonly now?: () => number;
}

export class ReplayRecorder {
  private readonly inputs: ReplayInput[] = [];
  private readonly events: ReplayRhythmEvent[] = [];
  private song: ReplaySong | null = null;
  private performance: ReplayPerformance | null = null;
  private recording = false;
  private droppedInputs = 0;
  private droppedEvents = 0;

  private readonly engineVersion: string;
  private readonly maxInputs: number;
  private readonly maxEvents: number;
  private readonly now: () => number;

  constructor(options: ReplayRecorderOptions = {}) {
    this.engineVersion = options.engineVersion ?? 'mandalizm-phase4';
    this.maxInputs = options.maxInputs ?? 20_000;
    this.maxEvents = options.maxEvents ?? 20_000;
    this.now = options.now ?? (() => Date.now());
  }

  get isRecording(): boolean {
    return this.recording;
  }

  get inputCount(): number {
    return this.inputs.length;
  }

  get eventCount(): number {
    return this.events.length;
  }

  /** Starts a run. Anything previously recorded is discarded. */
  begin(song: ReplaySong, performance: ReplayPerformance): void {
    this.inputs.length = 0;
    this.events.length = 0;
    this.droppedInputs = 0;
    this.droppedEvents = 0;
    this.song = song;
    this.performance = performance;
    this.recording = true;
    this.recordEvent({ time: 0, type: 'SONG_START' });
  }

  /**
   * A press.
   *
   * Every one, including presses that judged nothing. In `ART` those are the
   * whole point, and even in `SCORE` they are part of what the player did.
   */
  recordInput(input: ReplayInput): void {
    if (!this.recording) return;
    if (this.inputs.length >= this.maxInputs) {
      this.droppedInputs += 1;
      return;
    }
    this.inputs.push(input);
  }

  recordEvent(event: ReplayRhythmEvent): void {
    if (!this.recording) return;
    if (this.events.length >= this.maxEvents) {
      this.droppedEvents += 1;
      return;
    }
    this.events.push(event);
  }

  /** Ends the run and produces the file. Returns null if nothing was recorded. */
  finish(
    result: ReplayResult,
    extras: {
      readonly finalArtState?: ArtStateValues;
      readonly signalCounts?: Readonly<Record<string, number>>;
      readonly mutationCount?: number;
      readonly endTime?: number;
    } = {},
  ): ReplayData | null {
    if (this.song === null || this.performance === null) return null;
    this.recordEvent({ time: extras.endTime ?? 0, type: 'SONG_END' });
    this.recording = false;

    // Sorted on the way out. A live run records in time order anyway, but the
    // reader sorts defensively, and a file that changes shape when it is loaded
    // is not really a round trip — a replay must be identical to the replay it
    // was saved from, or nothing downstream can be compared against it.
    const byTime = <T extends { readonly time: number }>(items: readonly T[]): T[] =>
      [...items].sort((a, b) => a.time - b.time);

    return {
      version: REPLAY_SCHEMA_VERSION,
      song: this.song,
      performance: this.performance,
      timeline: {
        playerInputs: byTime(this.inputs),
        rhythmEvents: byTime(this.events),
      },
      result,
      metadata: {
        createdAt: this.now(),
        engineVersion: this.engineVersion,
        ...(extras.finalArtState === undefined ? {} : { finalArtState: extras.finalArtState }),
        ...(extras.signalCounts === undefined ? {} : { signalCounts: extras.signalCounts }),
        ...(extras.mutationCount === undefined ? {} : { mutationCount: extras.mutationCount }),
      },
    };
  }

  cancel(): void {
    this.recording = false;
    this.inputs.length = 0;
    this.events.length = 0;
    this.song = null;
    this.performance = null;
  }

  diagnostics(): {
    readonly recording: boolean;
    readonly inputs: number;
    readonly events: number;
    readonly droppedInputs: number;
    readonly droppedEvents: number;
  } {
    return {
      recording: this.recording,
      inputs: this.inputs.length,
      events: this.events.length,
      droppedInputs: this.droppedInputs,
      droppedEvents: this.droppedEvents,
    };
  }
}
